import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { DOCK_NOTE_KEYS, DOCK_NOTE_OFFSETS, DRUM_KEY_ROWS } from '@/audio/liveInput';

const noteName = (pitch) => `${['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][pitch % 12]}${Math.floor(pitch / 12) - 2}`;

/** Sound strip: pick the instrument the keys play, and step through sounds with the arrows or [ and ]. */
function SoundStrip({ armedType, current, synths = [], categories = [], kits = [], soundsFor = () => [], onPick, onCycle }) {
  const drums = armedType === 'drums';
  const [tab, setTab] = useState(current?.kind === 'sample' ? 'sounds' : 'synths');
  useEffect(() => { if (current?.kind === 'sample') setTab('sounds'); else if (current?.kind === 'synth') setTab('synths'); }, [current?.kind]);
  const pickCategory = (category) => {
    const list = soundsFor(category.id);
    const keep = current?.kind === 'sample' && current.category === category.id;
    const sound = keep ? list.find((item) => item.id === current.id) || list[0] : list[0];
    if (sound) onPick?.({ kind: 'sample', id: sound.id, category: sound.category, label: sound.name, sound });
  };
  return <div className="dock-sounds" data-testid="dock-sounds">
    <div className="dock-now">
      <small>Playing</small>
      <button aria-label="Previous sound" title="Previous sound ([)" onClick={() => onCycle?.(-1)}><ChevronLeft size={14} /></button>
      <strong data-testid="dock-current-sound">{current?.label || 'Pick a sound'}</strong>
      <button aria-label="Next sound" title="Next sound (])" onClick={() => onCycle?.(1)}><ChevronRight size={14} /></button>
      <kbd>[</kbd><kbd>]</kbd>
    </div>
    {!drums && <div className="dock-tabs" role="tablist" aria-label="Sound type">
      <button role="tab" aria-selected={tab === 'synths'} onClick={() => setTab('synths')}>Synths</button>
      <button role="tab" aria-selected={tab === 'sounds'} onClick={() => setTab('sounds')} disabled={!categories.length}>Your sounds</button>
    </div>}
    <div className="dock-chips" data-testid="dock-chips">
      {drums && kits.map((kit) => <button key={kit.id} className={current?.id === kit.id ? 'on' : ''} onClick={() => onPick?.({ kind: 'kit', id: kit.id, label: kit.name })}>{kit.name}</button>)}
      {!drums && tab === 'synths' && synths.map((voice) => <button key={voice.id} className={current?.kind === 'synth' && current.id === voice.id ? 'on' : ''} onClick={() => onPick?.({ kind: 'synth', id: voice.id, label: voice.label })}>{voice.label}</button>)}
      {!drums && tab === 'sounds' && categories.map((category) => <button key={category.id} className={current?.kind === 'sample' && current.category === category.id ? 'on' : ''} onClick={() => pickCategory(category)}>{category.label}</button>)}
    </div>
  </div>;
}

export function KeyboardDock({ open, armedType, octave, velocityLevel, heldKeys, midiSupported, midiName, onConnectMidi, onNoteOn, onNoteOff, onClose, sound = null }) {
  useEffect(() => {
    if (!open || !sound?.onCycle) return undefined;
    const keys = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target; if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      if (event.key === '[' || event.key === ']') { event.preventDefault(); sound.onCycle(event.key === '[' ? -1 : 1); }
    };
    window.addEventListener('keydown', keys);
    return () => window.removeEventListener('keydown', keys);
  }, [open, sound]);
  if (!open) return null;
  const drums = armedType === 'drums';
  return <section className="keyboard-dock" data-testid="keyboard-dock" aria-label="Computer keyboard instrument">
    <div className="keyboard-dock-info">
      <strong>{drums ? 'Finger drums' : 'Keyboard'}</strong>
      <span>Octave C{octave} <kbd>Z</kbd>/<kbd>X</kbd></span>
      <span>Velocity {velocityLevel}/5 <kbd>C</kbd>/<kbd>V</kbd></span>
      {midiSupported && <button onClick={onConnectMidi}>Connect MIDI</button>}
      <span data-testid="midi-status">{midiName ? `MIDI: ${midiName} connected` : 'No MIDI keyboard'}</span>
      <span className="keyboard-paused">Shortcuts paused while the keyboard is open</span>
      <button aria-label="Close keyboard" title="Close keyboard (Escape)" onClick={onClose}><X size={14} /></button>
    </div>
    {sound && <SoundStrip armedType={armedType} {...sound} />}
    <div className="keyboard-dock-keys">
      {DOCK_NOTE_KEYS.map((key) => {
        const pitch = (octave + 2) * 12 + DOCK_NOTE_OFFSETS[key];
        const drum = DRUM_KEY_ROWS[key];
        const black = ['w', 'e', 't', 'y', 'u', 'o', 'p'].includes(key);
        return <button key={key} data-key={key} className={`${black ? 'black' : 'white'}${heldKeys?.has(key) ? ' held' : ''}`}
          aria-label={drums && drum ? `${key.toUpperCase()} ${drum}` : `${key.toUpperCase()} ${noteName(pitch)}`}
          onPointerDown={(event) => { event.preventDefault(); onNoteOn?.(pitch, velocityLevel / 5, { token: `dock-${key}`, key }); }}
          onPointerUp={() => onNoteOff?.(pitch, { token: `dock-${key}` })} onPointerLeave={() => onNoteOff?.(pitch, { token: `dock-${key}` })}>
          <span>{drums && drum ? drum : noteName(pitch)}</span><kbd>{key === ';' ? ';' : key.toUpperCase()}</kbd>
        </button>;
      })}
    </div>
  </section>;
}

export default KeyboardDock;
